import { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";

/**
 * Hook pour récupérer les compteurs de notifications non lues par page
 * @param {string|number} hotelId - L'ID de l'hôtel (optionnel)
 * @returns {object} { countsByPage: object, loading: boolean, error: string|null }
 */
export const useNotificationCounts = (hotelId = null) => {
    const auth = useAuth();
    const [countsByPage, setCountsByPage] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCounts = async () => {
            if (!auth.isAuthenticated) {
                setCountsByPage({});
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const url = hotelId 
                    ? `/api/notifications/unread-counts-by-page?hId=${hotelId}`
                    : `/api/notifications/unread-counts-by-page`;

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: "Bearer " + auth.user?.access_token,
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    const counts = data.countsByPage || {};
                    setCountsByPage(counts);
                    setError(null);
                } else {
                    const errorText = await response.text();
                    console.error("❌ Failed to fetch notification counts:", {
                        status: response.status,
                        statusText: response.statusText,
                        error: errorText
                    });
                    setCountsByPage({});
                    setError("Failed to fetch notification counts");
                }
            } catch (err) {
                console.error("Error fetching notification counts:", err);
                setCountsByPage({});
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchCounts();

        return () => {};
    }, [hotelId, auth.isAuthenticated]);

    return { countsByPage, loading, error };
};

/**
 * Hook pour récupérer les notifications d'une page spécifique
 * @param {string} pageName - Le nom de la page (ex: "actions-list-v2")
 * @param {string|number} hotelId - L'ID de l'hôtel (optionnel)
 * @param {object} options - Options supplémentaires
 * @returns {object} { notifications: array, loading: boolean, error: string|null }
 */
export const usePageNotifications = (pageName, hotelId = null, options = {}) => {
    const { limit = 50, read = null } = options;
    const auth = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchNotifications = async () => {
            if (!auth.isAuthenticated || !pageName) {
                setNotifications([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                let url = `/api/notifications?pageName=${pageName}`;
                if (hotelId) url += `&hId=${hotelId}`;
                if (read !== null) url += `&read=${read}`;
                if (limit) url += `&limit=${limit}`;

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: "Bearer " + auth.user?.access_token,
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setNotifications(data.notifications || []);
                    setError(null);
                } else {
                    setNotifications([]);
                    setError("Failed to fetch notifications");
                }
            } catch (err) {
                console.error("Error fetching notifications:", err);
                setNotifications([]);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchNotifications();
        // NOTE: ne pas retrigger sur changement de token (effet refresh)
    }, [pageName, hotelId, read, limit, auth.isAuthenticated]);

    return { notifications, loading, error };
};

